<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />

    <div v-else>
      <!-- オプショングループ管理セクション -->
      <v-card class="mb-8">
        <v-card-title class="d-flex justify-space-between align-center">
          オプショングループ
          <v-dialog
            v-model="isFormOpen"
            width="600"
            persistent
          >
            <template #activator="{ on, attrs }">
              <v-btn
                fab
                small
                :ripple="false"
                color="primary"
                v-bind="attrs"
                v-on="on"
                @click="openCreateForm"
              >
                <v-icon>
                  {{ icons.mdiPlus }}
                </v-icon>
              </v-btn>
            </template>

            <v-card>
              <v-card-title>
                {{ form.id ? 'オプショングループ編集' : '新規オプショングループ' }}
              </v-card-title>

              <v-card-text>
                <v-text-field
                  v-model="form.name"
                  label="グループ名"
                  :error="!form.name"
                  placeholder="例: ドリンク選択、濃さ選択"
                />

                <v-text-field
                  v-model.number="form.position"
                  label="表示順"
                  type="number"
                  placeholder="0"
                />

                <v-switch
                  v-model="form.isRequired"
                  label="必須"
                  color="primary"
                  dense
                  hide-details
                  class="mb-4"
                />

                <v-radio-group
                  v-model="form.fieldType"
                  label="タイプ"
                  row
                  dense
                  hide-details
                  class="mb-4"
                >
                  <v-radio
                    label="選択式"
                    value="select"
                  />
                  <v-radio
                    label="数値入力式"
                    value="number"
                  />
                </v-radio-group>

                <v-divider class="mb-4" />

                <template v-if="form.fieldType !== 'number'">
                  <div class="d-flex align-center mb-2">
                    <span class="text-subtitle-1 font-weight-medium">オプション一覧</span>
                    <v-spacer />
                    <v-btn
                      small
                      outlined
                      color="primary"
                      @click="addOption"
                    >
                      <v-icon
                        small
                        left
                      >
                        {{ icons.mdiPlus }}
                      </v-icon>
                      オプション追加
                    </v-btn>
                  </div>

                  <div
                    v-for="(opt, idx) in form.vendibleOptionsAttributes"
                    :key="idx"
                    class="d-flex align-center mb-2"
                  >
                    <template v-if="!opt._destroy">
                      <v-text-field
                        v-model="opt.name"
                        :label="`オプション ${idx + 1}`"
                        :error="!opt.name"
                        outlined
                        dense
                        hide-details
                        class="mr-2"
                      />
                      <v-text-field
                        v-model.number="opt.position"
                        label="順"
                        type="number"
                        outlined
                        dense
                        hide-details
                        style="max-width: 80px;"
                        class="mr-2"
                      />
                      <v-btn
                        icon
                        small
                        @click="removeOption(idx)"
                      >
                        <v-icon small>
                          {{ icons.mdiTrashCanOutline }}
                        </v-icon>
                      </v-btn>
                    </template>
                  </div>

                  <div
                    v-if="activeOptions.length === 0"
                    class="text--secondary text-body-2 mt-2"
                  >
                    オプションがありません。「オプション追加」ボタンで追加してください。
                  </div>
                </template>

                <div
                  v-else
                  class="text--secondary text-body-2 mt-2"
                >
                  数値入力式のグループにはオプションを設定できません。
                </div>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="primary"
                  :ripple="false"
                  :disabled="!isFormValid"
                  :loading="isSubmitting"
                  @click="submit"
                >
                  {{ form.id ? '更新' : '作成' }}
                </v-btn>

                <v-btn
                  :ripple="false"
                  color="blue darken-1"
                  text
                  @click="cancelForm"
                >
                  キャンセル
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card-title>

        <v-card-text>
          <div
            v-if="optionGroups.length === 0"
            class="text--secondary"
          >
            オプショングループが登録されていません
          </div>

          <v-simple-table v-else>
            <thead>
              <tr>
                <th>グループ名</th>
                <th>タイプ</th>
                <th>オプション</th>
                <th>必須</th>
                <th class="text-center">
                  表示順
                </th>
                <th class="text-right" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="group in sortedGroups"
                :key="group.id"
              >
                <td>
                  {{ group.name }}
                </td>
                <td>
                  <v-chip
                    x-small
                    :color="group.field_type === 'number' ? 'info' : 'default'"
                    outlined
                  >
                    {{ group.field_type === 'number' ? '数値入力' : '選択式' }}
                  </v-chip>
                </td>
                <td>
                  <template v-if="group.field_type !== 'number'">
                    <v-chip
                      v-for="opt in group.options"
                      :key="opt.id"
                      x-small
                      outlined
                      class="mr-1"
                    >
                      {{ opt.name }}
                    </v-chip>
                  </template>
                  <span
                    v-else
                    class="text--secondary text-caption"
                  >
                    —
                  </span>
                </td>
                <td>
                  <v-icon
                    v-if="group.is_required"
                    small
                    color="success"
                  >
                    {{ icons.mdiCheck }}
                  </v-icon>
                </td>
                <td class="text-center">
                  {{ group.position }}
                </td>
                <td class="text-right">
                  <span class="d-flex align-center justify-end">
                    <v-btn
                      icon
                      :ripple="false"
                      @click="editGroup(group)"
                    >
                      <v-icon small>
                        {{ icons.mdiPencilOutline }}
                      </v-icon>
                    </v-btn>

                    <yona-edit-dialog
                      title="削除の確認"
                      btn-color="error"
                      save-text="同意の上で削除"
                      @save="destroyGroup(group.id)"
                    >
                      <template #append-outer-display-name>
                        <v-btn
                          icon
                          :ripple="false"
                        >
                          <v-icon small>
                            {{ icons.mdiTrashCanOutline }}
                          </v-icon>
                        </v-btn>
                      </template>

                      <template #input>
                        <span>
                          「{{ group.name }}」を削除してもよろしいですか
                        </span>
                      </template>
                    </yona-edit-dialog>
                  </span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <!-- 商品割り当てセクション -->
      <v-card v-if="optionGroups.length > 0 && vendibles.length > 0">
        <v-card-title>
          商品へのオプショングループ割り当て
        </v-card-title>

        <v-card-text>
          <div
            v-for="vendible in vendibles"
            :key="`${vendible.attributes.vendibleType}-${vendible.id}`"
            class="d-flex align-center mb-3"
          >
            <span
              class="mr-4 text-body-1"
              style="min-width: 160px;"
            >
              <v-chip
                x-small
                class="mr-1"
              >
                {{ vendibleTypeLabel(vendible.attributes.vendibleType) }}
              </v-chip>
              {{ vendible.attributes.name }}
            </span>
            <v-select
              :value="getAssignedGroupIds(vendible)"
              :items="rootGroupSelectItems"
              item-value="id"
              item-text="name"
              multiple
              dense
              hide-details
              clearable
              placeholder="なし"
              no-data-text="オプショングループがありません"
              :menu-props="{ auto: true, offsetY: true }"
              style="max-width: 400px;"
              @change="onAssignmentChange(vendible, $event)"
            >
              <template #selection="{ item }">
                <v-chip
                  small
                  close
                  class="mr-1"
                  @click:close="removeAssignment(vendible, item.id)"
                >
                  {{ item.name }}
                </v-chip>
              </template>
            </v-select>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiPlus,
  mdiPencilOutline,
  mdiTrashCanOutline,
  mdiCheck,
} from '@mdi/js'
import { findIndex, sortBy } from 'lodash'
import VendibleOptionGroupApi from '@/api/admin/VendibleOptionGroup'
import VendibleApi from '@/api/waiter/Vendible'
import useCompInit from '@/views/composable/useCompInit'
import YonaEditDialog from '@/views/components/util/YonaEditDialog.vue'

export default {
  components: {
    YonaEditDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { isLoading, initWith } = useCompInit()
    const isFormOpen = ref(false)
    const isSubmitting = ref(false)
    const optionGroups = ref([])
    const vendibles = ref([])

    const form = reactive({
      id: null,
      name: '',
      position: 0,
      isRequired: true,
      fieldType: 'select',
      vendibleOptionsAttributes: [],
    })

    const activeOptions = computed(() => {
      return form.vendibleOptionsAttributes.filter(o => !o._destroy)
    })

    const isFormValid = computed(() => {
      if (form.fieldType === 'number') return !!form.name

      return form.name && activeOptions.value.every(o => o.name)
    })

    const sortedGroups = computed(() => {
      return sortBy(optionGroups.value, ['position', 'id'])
    })

    const rootGroupSelectItems = computed(() => {
      return optionGroups.value.map(g => ({ id: g.id, name: g.name }))
    })

    const vendibleTypeLabel = type => {
      const labels = { Item: 'アイテム', Course: 'コース', Nomination: '指名' }

      return labels[type] || type
    }

    const getAssignedGroupIds = vendible => {
      const ids = []
      optionGroups.value.forEach(group => {
        if (group.assignments?.some(
          a => a.vendible_type === vendible.attributes.vendibleType && +a.vendible_id === +vendible.id,
        )) {
          ids.push(group.id)
        }
      })

      return ids
    }

    const onAssignmentChange = async (vendible, newGroupIds) => {
      const currentIds = getAssignedGroupIds(vendible)
      const toAssign = newGroupIds.filter(id => !currentIds.includes(id))
      const toUnassign = currentIds.filter(id => !newGroupIds.includes(id))

      const { vendibleType } = vendible.attributes
      const vendibleId = vendible.id

      const assignPromises = toAssign.map(groupId => VendibleOptionGroupApi.assignToVendible({
        id: groupId,
        vendibleType,
        vendibleId,
      }).then(res => {
        if (res?.data?.success) {
          const group = optionGroups.value.find(g => +g.id === +groupId)
          if (group) {
            if (!group.assignments) group.assignments = []
            group.assignments.push({ vendible_type: vendibleType, vendible_id: +vendibleId })
          }
        }
      }))

      const unassignPromises = toUnassign.map(groupId => VendibleOptionGroupApi.unassignFromVendible({
        id: groupId,
        vendibleType,
        vendibleId,
      }).then(res => {
        if (res?.data?.success) {
          const group = optionGroups.value.find(g => +g.id === +groupId)
          if (group && group.assignments) {
            group.assignments = group.assignments.filter(
              a => !(a.vendible_type === vendibleType && +a.vendible_id === +vendibleId),
            )
          }
        }
      }))

      await Promise.all([...assignPromises, ...unassignPromises])

      if (toAssign.length > 0 || toUnassign.length > 0) {
        vm.$toast.success('割り当てを更新しました')
      }
    }

    const removeAssignment = (vendible, groupId) => {
      const currentIds = getAssignedGroupIds(vendible).filter(id => id !== groupId)
      onAssignmentChange(vendible, currentIds)
    }

    const resetForm = () => {
      form.id = null
      form.name = ''
      form.position = 0
      form.isRequired = true
      form.fieldType = 'select'
      form.vendibleOptionsAttributes = []
    }

    const openCreateForm = () => {
      resetForm()
    }

    const cancelForm = () => {
      isFormOpen.value = false
      resetForm()
    }

    const addOption = () => {
      form.vendibleOptionsAttributes.push({
        name: '',
        position: activeOptions.value.length,
      })
    }

    const removeOption = idx => {
      const opt = form.vendibleOptionsAttributes[idx]
      if (opt.id) {
        opt._destroy = true
      } else {
        form.vendibleOptionsAttributes.splice(idx, 1)
      }
    }

    const editGroup = group => {
      form.id = group.id
      form.name = group.name
      form.position = group.position
      form.isRequired = group.is_required
      form.fieldType = group.field_type || 'select'
      form.vendibleOptionsAttributes = group.options.map(o => ({
        id: o.id,
        name: o.name,
        position: o.position,
      }))
      isFormOpen.value = true
    }

    const submit = async () => {
      isSubmitting.value = true
      const isUpdate = !!form.id
      const payload = {
        id: form.id,
        name: form.name,
        position: form.position,
        isRequired: form.isRequired,
        fieldType: form.fieldType,
        vendibleOptionsAttributes: form.fieldType === 'number' ? [] : form.vendibleOptionsAttributes,
      }

      const res = isUpdate
        ? await VendibleOptionGroupApi.updateVendibleOptionGroup(payload)
        : await VendibleOptionGroupApi.createVendibleOptionGroup(payload)

      if (res?.data?.vendible_option_group) {
        const group = res.data.vendible_option_group
        if (isUpdate) {
          const idx = findIndex(optionGroups.value, o => +o.id === +group.id)
          optionGroups.value.splice(idx, 1, group)
        } else {
          optionGroups.value.push(group)
        }
        vm.$toast.success(isUpdate ? 'オプショングループを更新しました' : 'オプショングループを追加しました')
      }

      isSubmitting.value = false
      cancelForm()
    }

    const destroyGroup = async id => {
      const res = await VendibleOptionGroupApi.deleteVendibleOptionGroup(id)

      if (res?.data?.status === 'error') {
        vm.$toast.error('削除できません')

        return
      }

      if (res) {
        const idx = findIndex(optionGroups.value, o => +o.id === +id)
        optionGroups.value.splice(idx, 1)
        vm.$toast.success('オプショングループを削除しました')
      }
    }

    const getOptionGroups = async () => {
      const res = await VendibleOptionGroupApi.getVendibleOptionGroups()
      if (res?.status === 200) {
        optionGroups.value = [...res.data.vendible_option_groups]
      }
    }

    const getVendibles = async () => {
      const res = await VendibleApi.getVendibles()
      if (res?.data) {
        vendibles.value = [...res.data.vendibles.data]
      }
    }

    initWith([getOptionGroups(), getVendibles()])

    return {
      isLoading,
      isFormOpen,
      isSubmitting,
      optionGroups,
      vendibles,
      form,
      activeOptions,
      isFormValid,
      sortedGroups,
      rootGroupSelectItems,
      vendibleTypeLabel,
      getAssignedGroupIds,
      onAssignmentChange,
      removeAssignment,
      openCreateForm,
      cancelForm,
      addOption,
      removeOption,
      editGroup,
      submit,
      destroyGroup,
      icons: {
        mdiPlus,
        mdiPencilOutline,
        mdiTrashCanOutline,
        mdiCheck,
      },
    }
  },
}
</script>
